import axios from 'axios';

export {
  metricsFromAggregates,
  calculateTicketsMetrics,
  filterTicketsBySearch,
  getEffectiveScopeForPipelines,
  mapScopeToDropdownFlag,
  type TicketsScope,
} from './ticketsAdapter';

export {
  computeStageClock,
  formatStageClock,
  type StageClockLevel,
  type StageClockView,
} from './stageClock';

/** Strip HTML tags and decode entities for plain text display */
export function stripHtml(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
}

/**
 * Extrai a mensagem real do backend de um erro Axios (`data.message` /
 * `data.error`), com fallback pro texto genérico do caller. Mesmo shape do
 * `getErrorMessage` interno de `@/lib/api` (não exportado) — precedente
 * module-local: billing (`useCards`/`useCredits`).
 *
 * Usar em catches de chamadas com `_handleErrorLocally` (interceptor global
 * suprimido) — ex.: `ticketsService.transfer`, onde o 400 "Invalid transfer
 * payload" do same-pipeline precisa chegar legível ao usuário.
 */
export function getApiErrorMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error) && error.response?.data) {
    const data = error.response.data as Record<string, unknown>;
    if (typeof data.message === 'string') return data.message;
    if (typeof data.error === 'string') return data.error;
  }
  return fallback;
}

/**
 * Idade humana em pt-BR na escala EXATA dos mockups Helpdesk 01 e 05:
 * "agora" · "há 5 min" · "há 40 min" · "há 3 horas" · "há 2 dias" · "há 3 sem".
 *
 * Nenhum dos dois formatadores existentes serve: `formatTimeAgo` de `@/lib/utils`
 * devolve "5m"/"3h" (sem o "há", `src/lib/utils.ts:19-44`) e o
 * `formatDistanceToNow(..., { locale: ptBR })` do date-fns devolve
 * "há cerca de 3 horas" e "há 5 minutos" — os mockups usam "há 3 horas" e
 * "há 5 min" (`comparacao.html:1173` e `:1369`).
 */
export function formatStageAge(iso?: string): string {
  if (!iso) return 'sem data';
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return 'data inválida';
  if (ms < 60_000) return 'agora';
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 60) return `há ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `há ${hours} ${hours === 1 ? 'hora' : 'horas'}`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `há ${days} ${days === 1 ? 'dia' : 'dias'}`;
  return `há ${Math.floor(days / 7)} sem`;
}
